import { cn } from "./cn";

export type LoaderProps = {
  variant?: "dots" | "typing" | "text-shimmer" | "circular";
  size?: "sm" | "md" | "lg";
  text?: string;
  className?: string;
};

const dotSizes = { sm: "h-1.5 w-1.5", md: "h-2 w-2", lg: "h-2.5 w-2.5" };
const textSizes = { sm: "text-xs", md: "text-sm", lg: "text-base" };
const ringSizes = { sm: "h-4 w-4", md: "h-5 w-5", lg: "h-6 w-6" };

function DotsLoader({
  size = "md",
  className,
}: Omit<LoaderProps, "variant" | "text">) {
  return (
    <div
      className={cn("inline-flex items-center gap-1.5", className)}
      role="status"
    >
      {[0, 200, 400].map(delay => (
        <span
          key={delay}
          className={cn(
            "animate-bounce rounded-full bg-gray-400",
            dotSizes[size!]
          )}
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

function TypingLoader({
  size = "md",
  className,
}: Omit<LoaderProps, "variant" | "text">) {
  return (
    <div
      className={cn("inline-flex items-center gap-1", className)}
      role="status"
    >
      {[0, 250, 500].map(delay => (
        <span
          key={delay}
          className={cn(
            "animate-pulse rounded-full bg-gray-400",
            dotSizes[size!]
          )}
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
      <span className="sr-only">Loading</span>
    </div>
  );
}

function TextShimmerLoader({
  text = "Thinking",
  size = "md",
  className,
}: Omit<LoaderProps, "variant">) {
  return (
    <div
      role="status"
      className={cn(
        "animate-shimmer bg-clip-text font-medium text-transparent",
        textSizes[size!],
        className
      )}
      style={{
        backgroundImage:
          "linear-gradient(to right, #9CA3AF 40%, #1F2937 50%, #9CA3AF 60%)",
        backgroundSize: "200% auto",
      }}
    >
      {text}
    </div>
  );
}

function CircularLoader({
  size = "md",
  className,
}: Omit<LoaderProps, "variant" | "text">) {
  return (
    <div
      role="status"
      className={cn(
        "animate-spin rounded-full border-2 border-gray-200 border-t-gray-400",
        ringSizes[size!],
        className
      )}
    >
      <span className="sr-only">Loading</span>
    </div>
  );
}

export function Loader({
  variant = "dots",
  size = "md",
  text,
  className,
}: LoaderProps) {
  switch (variant) {
    case "typing":
      return <TypingLoader size={size} className={className} />;
    case "text-shimmer":
      return (
        <TextShimmerLoader size={size} text={text} className={className} />
      );
    case "circular":
      return <CircularLoader size={size} className={className} />;
    default:
      return <DotsLoader size={size} className={className} />;
  }
}
